/*
 *
 * Copyright (c) 1999-2021 Luciad All Rights Reserved.
 *
 * Luciad grants you ("Licensee") a non-exclusive, royalty free, license to use,
 * modify and redistribute this software in source and binary code form,
 * provided that i) this copyright notice and license appear on all copies of
 * the software; and ii) Licensee does not utilize the software in a manner
 * which is disparaging to Luciad.
 *
 * This software is provided "AS IS," without a warranty of any kind. ALL
 * EXPRESS OR IMPLIED CONDITIONS, REPRESENTATIONS AND WARRANTIES, INCLUDING ANY
 * IMPLIED WARRANTY OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE OR
 * NON-INFRINGEMENT, ARE HEREBY EXCLUDED. LUCIAD AND ITS LICENSORS SHALL NOT BE
 * LIABLE FOR ANY DAMAGES SUFFERED BY LICENSEE AS A RESULT OF USING, MODIFYING
 * OR DISTRIBUTING THE SOFTWARE OR ITS DERIVATIVES. IN NO EVENT WILL LUCIAD OR ITS
 * LICENSORS BE LIABLE FOR ANY LOST REVENUE, PROFIT OR DATA, OR FOR DIRECT,
 * INDIRECT, SPECIAL, CONSEQUENTIAL, INCIDENTAL OR PUNITIVE DAMAGES, HOWEVER
 * CAUSED AND REGARDLESS OF THE THEORY OF LIABILITY, ARISING OUT OF THE USE OF
 * OR INABILITY TO USE SOFTWARE, EVEN IF LUCIAD HAS BEEN ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGES.
 */
import {addSlider, paramExists, ThemedPanel} from "./ThemedPanel";

const THEME_FOCUS = 1;
const THEME_DEPTH = 2;
const THEME_COLORS = 3;

const SLIDER_OPTIONS_FOCUS_DISTANCE = {
  label: "Vzdálenost:",
  value: "500",
  min: "0",
  max: "3000",
  step: "50"
};
const SLIDER_OPTIONS_FOCUS_RANGE = {
  label: "Rozsah:",
  value: "500",
  min: "50",
  max: "1500",
  step: "50"
};

const SLIDER_OPTIONS_DEPTH_PERCEPTION_WINDOW = {
  label: "Šířka:",
  value: "2",
  min: "1",
  max: "5",
  step: "1"
};

const SLIDER_OPTIONS_DEPTH_PERCEPTION_STRENGTH = {
  label: "Kontrast:",
  value: "0.5",
  min: "0.1",
  max: "3.0",
  step: "0.1"
};

const SLIDER_OPTIONS_COLORS_SATURATION = {
  label: "Saturace:",
  value: "100",
  min: "0",
  max: "200",
  step: "5",
  valueFormatter: formatterPerCent
};

const SLIDER_OPTIONS_COLORS_BRIGHTNESS = {
  label: "Jas:",
  value: "100",
  min: "50",
  max: "200",
  step: "5",
  valueFormatter: formatterPerCent
};

const SLIDER_OPTIONS_COLORS_CONTRAST = {
  label: "Kontrast:",
  value: "100",
  min: "50",
  max: "200",
  step: "5",
  valueFormatter: formatterPerCent
};

const THEMES = [{
  id: THEME_FOCUS,
  text: "Hloubka ostrosti",
  container: "focus",
  only3D: true,
  init: (panelDomNode, themeControl, map) => {
    const dofFocusSlider = addSlider(panelDomNode, SLIDER_OPTIONS_FOCUS_DISTANCE);
    const dofRangeSlider = addSlider(panelDomNode, SLIDER_OPTIONS_FOCUS_RANGE);

    const onChange = () => {
      const active = themeControl.isActive();
      if (active) {
        applyDOF(map, dofFocusSlider.value, dofRangeSlider.value);
      } else {
        applyDOF(map, null);
      }
      dofFocusSlider.disabled = !active;
      dofRangeSlider.disabled = !active;
    };

    dofFocusSlider.addHandler(onChange);
    dofRangeSlider.addHandler(onChange);

    return onChange;
  },
}, {
  id: THEME_DEPTH,
  text: "Zvýraznění",
  container: "perception",
  only3D: true,
  init: (panelDomNode, themeControl, map) =>  {
    const edlWindowComponent = addSlider(panelDomNode, SLIDER_OPTIONS_DEPTH_PERCEPTION_WINDOW);
    const edlStrengthComponent = addSlider(panelDomNode, SLIDER_OPTIONS_DEPTH_PERCEPTION_STRENGTH);

    const onChange = () => {
      const active = themeControl.isActive();
      if (active) {
        applyEDL(map, edlWindowComponent.value, edlStrengthComponent.value);
      } else {
        applyEDL(map, null);
      }
      edlWindowComponent.disabled = !active;
      edlStrengthComponent.disabled = !active;
    }

    edlWindowComponent.addHandler(onChange);
    edlStrengthComponent.addHandler(onChange);

    return onChange;
  }
}, {
  id: THEME_COLORS,
  text: "Barvy",
  container: "colors",
  init: (panelDomNode, themeControl, map) => {
    const saturationComponent = addSlider(panelDomNode, SLIDER_OPTIONS_COLORS_SATURATION);
    const brightnessComponent = addSlider(panelDomNode, SLIDER_OPTIONS_COLORS_BRIGHTNESS);
    const contrastComponent = addSlider(panelDomNode, SLIDER_OPTIONS_COLORS_CONTRAST);

    const onChange = () => {
      const active = themeControl.isActive();
      if (active) {
        const canvasFilterStyle =
            "saturate(" + saturationComponent.value + "%)" +
            "contrast(" + contrastComponent.value + "%)" +
            "brightness(" + brightnessComponent.value + "%)";
        applyStyleOnCanvas(map, canvasFilterStyle);
      } else {
        applyStyleOnCanvas(map, null);
      }
      saturationComponent.disabled = !active;
      brightnessComponent.disabled = !active;
      contrastComponent.disabled = !active;
    }

    saturationComponent.addHandler(onChange);
    brightnessComponent.addHandler(onChange);
    contrastComponent.addHandler(onChange);

    return onChange;
  }
}];

function formatterPerCent(v) {
  return v + "%";
}

function applyDOF(map, dofFocus, dofRange) {
  if (paramExists(dofFocus) && paramExists(dofRange)) {
    map.effects.depthOfField = {
      focalDepth: dofFocus,
      focusRange: dofRange
    };
  } else {
    map.effects.depthOfField = null;
  }
}

function applyEDL(map, edlWindow, edlStrength) {
  if (paramExists(edlWindow) && paramExists(edlStrength)) {
    map.effects.eyeDomeLighting = {
      window: edlWindow,
      strength: edlStrength
    };
  } else {
    map.effects.eyeDomeLighting = null;
  }
}

/**
 * Applies a filter style on a canvas. For more information on this, have a look at
 * https://developer.mozilla.org/en-US/docs/Web/CSS/filter
 *
 * @param map the map
 * @param canvasFilterStyle a string that represents the CSS style filter to be applied
 */
function applyStyleOnCanvas(map, canvasFilterStyle) {
  const canvasElements = map.domNode.getElementsByTagName("canvas");
  for (let i = 0; i < canvasElements.length; i++) {
    const canvasElement = canvasElements[i];
    canvasElement.style.filter = canvasFilterStyle;
  }
}

export class EffectsPanel extends ThemedPanel {
  constructor(map, options) {
    options = options || {};
    options.title = options.title || "EFEKTY";
    options.icon = options.icon || "lcdIconLineColor";
    super(map, options, THEMES);
    this.init(map, options);
  }
}